import React, { useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { icon } from "@fortawesome/fontawesome-svg-core/import.macro";
import Badge from "../../components/ui-components/Badge";
import { toast } from "../../components/ui-components/Toast";
import {
  listSchedulesByType,
  triggerSchedule,
  pauseSchedule,
  resumeSchedule,
} from "./api";
import { formatRelativeTime, cronToHuman } from "./utils";

function mapBadgeStatus(status) {
  if (status === "success") return "success";
  if (status === "failed") return "failed";
  if (status === "running") return "running";
  if (status === "paused") return "stopped";
  if (status === "active") return "success";
  return "pending";
}

function statusLabel(status) {
  if (!status) return "—";
  return status.charAt(0).toUpperCase() + status.slice(1);
}

const ReportingTab = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const { data, isLoading } = useQuery({
    queryKey: ["schedules", "report_generation"],
    queryFn: () => listSchedulesByType("report_generation"),
    staleTime: 60000,
  });

  const schedules = data?.schedules || [];

  const invalidate = useCallback(() => {
    queryClient.invalidateQueries({ queryKey: ["schedules", "report_generation"] });
  }, [queryClient]);

  const triggerMutation = useMutation({
    mutationFn: triggerSchedule,
    onSuccess: () => { toast.success("Job triggered"); invalidate(); },
    onError: (err) => toast.error(err?.message || "Failed to trigger job"),
  });

  const pauseResumeMutation = useMutation({
    mutationFn: ({ id, isPaused }) => (isPaused ? resumeSchedule(id) : pauseSchedule(id)),
    onSuccess: (_, { isPaused }) => {
      toast.success(isPaused ? "Job resumed" : "Job paused");
      invalidate();
    },
    onError: (err) => toast.error(err?.message || "Action failed"),
  });

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-text-2" />
      </div>
    );
  }

  if (schedules.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center h-60">
        <FontAwesomeIcon icon={icon({ name: "file-chart-column", style: "light" })} className="text-4xl text-text-3 mb-4" />
        <p className="text-lg font-medium text-text mb-1">No reporting jobs</p>
        <p className="text-sm text-text-2">Reporting jobs will appear here once configured.</p>
      </div>
    );
  }

  return (
    <div className="mr-6">
      <div className="bg-card rounded-card border border-card-bd overflow-hidden">
        <div className="flex items-center h-[36px] pl-5 pr-4 text-text-2">
          <span className="w-[35%] uppercase text-xs tracking-wide font-medium">Job</span>
          <span className="w-[15%] uppercase text-xs tracking-wide font-medium">Status</span>
          <span className="w-[15%] uppercase text-xs tracking-wide font-medium">Last Run</span>
          <span className="w-[15%] uppercase text-xs tracking-wide font-medium">Result</span>
          <span className="w-[10%] uppercase text-xs tracking-wide font-medium">Schedule</span>
          <span className="w-[10%] uppercase text-xs tracking-wide font-medium text-right pr-2">Actions</span>
        </div>
        {schedules.map((job, i) => {
          const isPaused = job.current_status === "paused";
          const isLast = i === schedules.length - 1;
          return (
            <div
              key={job.id}
              className={`flex items-center h-[64px] pl-5 pr-4 border-t border-border cursor-pointer transition-colors text-sm hover:bg-primary-soft ${
                isLast ? "rounded-b-[16px]" : ""
              }`}
              onClick={() => navigate(`/reporting-jobs/${job.id}`)}
              onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); navigate(`/reporting-jobs/${job.id}`); } }}
              tabIndex={0}
              role="button"
              aria-label={`Open job ${job.job_name || job.job_id}`}
            >
              <div className="w-[35%] min-w-0 pr-4">
                <div className="font-medium text-text truncate">{job.job_name || job.job_id}</div>
                <div className="text-xs text-text-2 truncate mt-0.5">
                  {job.job_id}{job.metadata?.credential_type ? ` · ${job.metadata.credential_type.replaceAll("_", " ")}` : ""}
                </div>
              </div>
              <div className="w-[15%] flex items-center">
                <div className="w-fit"><Badge status={mapBadgeStatus(job.current_status)} label={statusLabel(job.current_status)} /></div>
              </div>
              <div className="w-[15%] text-text-2">
                {formatRelativeTime(job.last_run_timestamp)}
              </div>
              <div className="w-[15%] flex items-center">
                {job.last_run_status ? (
                  <div className="w-fit"><Badge status={mapBadgeStatus(job.last_run_status)} label={statusLabel(job.last_run_status)} /></div>
                ) : (
                  <span className="text-text-2">—</span>
                )}
              </div>
              <div className="w-[10%] text-text-2">
                {isPaused ? "Paused" : (formatRelativeTime(job.next_run_timestamp) !== "—" ? formatRelativeTime(job.next_run_timestamp) : cronToHuman(job.schedule_expression))}
              </div>
              <div className="w-[10%] flex justify-end" onClick={(e) => e.stopPropagation()}>
                <div className="flex items-center gap-1">
                  <button
                    type="button"
                    onClick={() => triggerMutation.mutate(job.id)}
                    className="p-1.5 text-text-2 hover:text-text hover:bg-surface-2 rounded-ctrl transition-colors"
                    title="Run now"
                    aria-label="Run now"
                  >
                    <FontAwesomeIcon icon={icon({ name: "play", style: "light" })} className="w-3.5 h-3.5" />
                  </button>
                  <button
                    type="button"
                    onClick={() => pauseResumeMutation.mutate({ id: job.id, isPaused })}
                    className="p-1.5 text-text-2 hover:text-text hover:bg-surface-2 rounded-ctrl transition-colors"
                    title={isPaused ? "Resume" : "Pause"}
                    aria-label={isPaused ? "Resume" : "Pause"}
                  >
                    {isPaused ? (
                      <FontAwesomeIcon icon={icon({ name: "circle-play", style: "light" })} className="w-3.5 h-3.5" />
                    ) : (
                      <FontAwesomeIcon icon={icon({ name: "pause", style: "light" })} className="w-3.5 h-3.5" />
                    )}
                  </button>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default ReportingTab;
